/**
 * Online Food Ordering Type Definitions
 * Shared types for the application
 */

// ============================================
// ENUMS
// ============================================

export enum OrderType {
  DELIVERY = 'delivery',
  COLLECTION = 'collection',
}

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PREPARING = 'preparing',
  READY = 'ready',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  COLLECTED = 'collected',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export enum ModifierType {
  SINGLE_REQUIRED = 'single_required',
  SINGLE_OPTIONAL = 'single_optional',
  MULTI_OPTIONAL = 'multi_optional',
}

// ============================================
// USER & AUTH
// ============================================

export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role: 'customer' | 'staff' | 'admin' | 'super_admin';
  email_verified: boolean;
  marketing_email_consent: boolean;
  marketing_sms_consent: boolean;
  push_notification_consent: boolean;
  created_at: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface Address {
  id: string;
  label: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  postcode: string;
  country: string;
  delivery_instructions?: string;
  is_default: boolean;
}

export interface AddressInput {
  label?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  postcode: string;
  delivery_instructions?: string;
  is_default?: boolean;
}

// ============================================
// RESTAURANT & MENU
// ============================================

export interface Restaurant {
  id: string;
  slug: string;
  // language-neutral fields
  email: string;
  postcode: string;
  latitude?: number;
  longitude?: number;
  logo_url?: string;
  hero_image_url?: string;
  primary_color: string;
  // Translation fields — single source of truth (no plain name/description/address/phone)
  name_translations: Record<string, string>;
  phone_translations: Record<string, string>;
  promo_banner_text_translations: Record<string, string>;
  description_translations: Record<string, string>;
  // address_translations: {"en": {"address_line_1": "...", "city": "..."}, "ar": {...}}
  address_translations: Record<string, { address_line_1?: string; address_line_2?: string; city?: string }>;
  opening_hours: Record<string, { open: string; close: string }>;
  // Square Payment (from OAuth connection)
  square_location_id?: string;
  // Legacy fee fields (kept for backward compatibility)
  service_fee_percent: number;
  service_fee_min: number;
  service_fee_max: number;
  delivery_fee: number;
  free_delivery_threshold: number;
  minimum_order: number;
  // Rule-based fee systems (optional)
  service_fee_rules?: Array<{
    id: string;
    name: string;
    min_amount: number;
    max_amount: number | null;
    fee_type: 'flat' | 'percentage';
    fee_value: number;
    min_fee?: number;
    max_fee?: number;
    enabled: boolean;
  }>;
  delivery_fee_rules?: Array<{
    id: string;
    name: string;
    max_distance_miles: number | null;
    fee_type: 'flat' | 'percentage';
    fee_value: number;
    min_delivery_fee?: number | null;
    max_delivery_fee?: number | null;
    min_order: number;
    free_delivery_threshold: number;
    enabled: boolean;
  }>;
  accepts_delivery: boolean;
  accepts_collection: boolean;
  is_open: boolean;
  accepts_preorders: boolean;
  prep_time_minutes: number;
  delivery_radius_miles: number;
  // Business & Social
  business_website?: string;
  social_facebook?: string;
  social_instagram?: string;
  social_twitter?: string;
  // Legal
  privacy_policy_url?: string;
  terms_url?: string;
  unsubscribe_url?: string;
}

export interface Category {
  id: string;
  slug: string;
  name_translations: Record<string, string>;
  description_translations?: Record<string, string>;
  image_url?: string;
  display_order: number;
  is_active: boolean;
  items: MenuItem[];
}

export interface CategoryBrief {
  id: string;
  slug: string;
  name_translations: Record<string, string>;
  image_url?: string;
  item_count: number;
}

export interface MenuItem {
  id: string;
  category_id: string;
  slug: string;
  name_translations: Record<string, string>;
  description_translations?: Record<string, string>;
  price: number;
  compare_at_price?: number;
  image_url?: string;
  thumbnail_url?: string;
  is_vegetarian: boolean;
  is_vegan: boolean;
  is_halal: boolean;
  is_gluten_free: boolean;
  is_spicy: boolean;
  spice_level?: number;
  allergens_translations: Array<Record<string, string>>;
  is_available: boolean;
  is_featured: boolean;
  is_popular: boolean;
  display_order: number;
  modifier_groups?: ModifierGroup[];
}

export interface ModifierGroup {
  id: string;
  name_translations: Record<string, string>;
  description_translations: Record<string, string>;
  modifier_type: ModifierType;
  min_selections: number;
  max_selections?: number;
  display_order: number;
  is_active: boolean;
  options: ModifierOption[];
}

export interface ModifierOption {
  id: string;
  name_translations: Record<string, string>;
  price_adjustment: number;
  is_default: boolean;
  is_available: boolean;
  display_order: number;
  allergens_translations?: Array<Record<string, string>>;
}

// ============================================
// CART
// ============================================

export interface CartModifier {
  group_id: string;
  option_id: string;
  name: string;
  price_adjustment: number;
}

export interface CartItem {
  id: string; // Client-side ID for tracking
  menu_item_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  image_url?: string;
  modifiers: CartModifier[];
  notes?: string;
}

export interface Cart {
  restaurant_id: string;
  items: CartItem[];
  promo_code?: string;
}

// ============================================
// ORDERS
// ============================================

export interface OrderItem {
  id: string;
  menu_item_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  modifiers: CartModifier[];
  modifiers_total: number;
  line_total: number;
  notes?: string;
}

export interface OrderStatusHistory {
  status: OrderStatus;
  notes?: string;
  created_at: string;
}

export interface Order {
  id: string;
  order_number: string;
  order_type: OrderType;
  status: OrderStatus;
  subtotal: number;
  service_fee: number;
  delivery_fee: number;
  discount_amount: number;
  total: number;
  promo_code_used?: string;
  payment_status: PaymentStatus;
  payment_method?: string;
  delivery_address?: AddressInput;
  delivery_instructions?: string;
  scheduled_for?: string;
  created_at: string;
  confirmed_at?: string;
  prepared_at?: string;
  ready_at?: string;
  completed_at?: string;
  items: OrderItem[];
  status_history: OrderStatusHistory[];
}

export interface OrderBrief {
  id: string;
  order_number: string;
  order_type: OrderType;
  status: OrderStatus;
  total: number;
  payment_status: PaymentStatus;
  created_at: string;
  item_count: number;
}

// ============================================
// PROMO CODES
// ============================================

export interface PromoCodeResult {
  valid: boolean;
  code?: string;
  discount_amount: number;
  message: string;
}

// ============================================
// PAYMENT
// ============================================

export interface PaymentIntent {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
}

// ============================================
// COOKIE CONSENT
// ============================================

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

// ============================================
// API RESPONSES
// ============================================

export interface ApiError {
  detail: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// ============================================
// WEBSOCKET
// ============================================

export interface OrderUpdateMessage {
  type: 'order_update' | 'new_order';
  order_id: string;
  status?: OrderStatus;
  order_number?: string;
  total?: number;
  timestamp: number;
}
